import { useState } from "react";
import { AlertTriangle, CheckCircle2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { useVisibleT } from "@/lib/cms/cms-new-i18n";
import { cn } from "@/lib/utils";
import {
  useAnswerImportDecisions,
  useApplyBulkImportDecision,
  useImportDecisionGroups,
  useImportDecisions,
} from "@/api/import";
import {
  ImportAnalysisReport,
  ImportDecisionAnswer,
  ImportDecisionState,
} from "@/lib/import/import-types";
import { ImportDecisionRow } from "../components/ImportDecisionRow";

/**
 * Etape d'arbitrage des liaisons.
 *
 * L'unite de travail est la **valeur distincte**, pas la ligne : le serveur
 * regroupe, donc « Arthur raimbaud » present dans 37 lignes ne pose qu'**une**
 * question. C'est la difference entre une soixantaine de decisions et plus d'un
 * millier.
 *
 * Le filtre par defaut est « à confirmer » : on depose l'utilisateur dans le
 * travail qui reste, pas dans la liste de ce qui est deja resolu.
 */
const PAGE_SIZE = 25;

export const ImportStepRelations = ({
  jobId,
  report,
  onNextStep,
  onBack,
}: {
  jobId: string;
  report: ImportAnalysisReport;
  onNextStep: () => void;
  onBack: () => void;
}) => {
  const vt = useVisibleT();
  const { toast } = useToast();

  const [columnKey, setColumnKey] = useState<string | undefined>(undefined);
  const [state, setState] = useState<ImportDecisionState | undefined>("pending");
  const [page, setPage] = useState(0);

  const { data: groups, isLoading: groupsLoading } = useImportDecisionGroups(jobId);
  const { data, isLoading } = useImportDecisions(jobId, {
    columnKey,
    state,
    take: PAGE_SIZE,
    skip: page * PAGE_SIZE,
  });

  const { mutateAsync: answer, isPending: answering } = useAnswerImportDecisions();
  const { mutateAsync: applyBulk, isPending: bulking } = useApplyBulkImportDecision();

  const labelFor = (key: string) =>
    report.columns.find((column) => column.fieldKey === key.split(".")[0])
      ?.fieldLabel ?? key;

  /**
   * Seule source de verite du reste a faire : les compteurs **du serveur**,
   * reinvalides a chaque reponse.
   *
   * Ne jamais y melanger un compteur fige a l'analyse : il ne se met pas a jour
   * quand l'utilisateur repond, et la porte resterait fermee pour toujours.
   */
  const totalPending = groups?.reduce((sum, group) => sum + group.pending, 0) ?? 0;

  const submit = async (decisions: ImportDecisionAnswer[]) => {
    try {
      await answer({ jobId, decisions });
    } catch {
      toast({
        variant: "destructive",
        title: vt(
          "bo.import.decisions.saveError",
          "Votre choix n'a pas pu être enregistré.",
        ),
      });
    }
  };

  const bulk = async (
    scope: "allAuto" | "allPending" | "allWithoutMatch",
    action: "link" | "skip" | "create",
  ) => {
    try {
      const result = await applyBulk({ jobId, columnKey, scope, action });
      toast({
        title: `${result.affected} ${vt(
          "bo.import.decisions.bulkApplied",
          "correspondances appliquées",
        )}`,
      });
    } catch {
      toast({
        variant: "destructive",
        title: vt("bo.import.decisions.bulkError", "L'action groupée a échoué."),
      });
    }
  };

  if (groupsLoading) {
    return (
      <div className="flex flex-col gap-3">
        <Skeleton className="h-8 w-1/3" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  // Aucune colonne de liaison : l'etape ne s'auto-saute pas silencieusement, le
  // fil d'Ariane semblerait sauter tout seul.
  if (!groups || groups.length === 0) {
    return (
      <div className="flex flex-col gap-6 max-w-2xl">
        <div className="flex gap-3 p-4 rounded-xl border border-green-300 bg-green-50 text-sm text-green-900">
          <CheckCircle2 className="w-5 h-5 shrink-0" aria-hidden />
          <span>
            {vt(
              "bo.import.relations.nothingToConfirm",
              "Aucune colonne de liaison dans votre fichier : il n'y a rien à confirmer.",
            )}
          </span>
        </div>
        <div className="flex justify-between">
          <Button variant="ghost" onClick={onBack}>
            {vt("bo.import.back", "Retour")}
          </Button>
          <Button onClick={onNextStep}>{vt("bo.import.continue", "Continuer")}</Button>
        </div>
      </div>
    );
  }

  const filters: Array<{ key: ImportDecisionState | undefined; label: string }> = [
    { key: "pending", label: vt("bo.import.decisions.filterPending", "À confirmer") },
    { key: "auto", label: vt("bo.import.decisions.filterAuto", "Reconnues") },
    { key: "resolved", label: vt("bo.import.decisions.filterResolved", "Décidées") },
    { key: undefined, label: vt("bo.import.decisions.filterAll", "Toutes") },
  ];

  return (
    <div className="flex flex-col gap-5">
      <p className="text-sm text-gray-600 max-w-3xl">
        {vt(
          "bo.import.relations.intro",
          "Une question par valeur différente, pas par ligne : confirmer une fois s'applique à toutes les lignes concernées.",
        )}
      </p>

      <div className="flex gap-6 flex-wrap lg:flex-nowrap">
        {/* Rail des champs : où reste-t-il du travail ? */}
        <nav
          className="flex flex-col gap-1 w-full lg:w-64 shrink-0"
          aria-label={vt("bo.import.relations.fields", "Champs de liaison")}
        >
          <button
            type="button"
            onClick={() => {
              setColumnKey(undefined);
              setPage(0);
            }}
            className={cn(
              "text-left px-3 py-2 rounded-lg text-sm",
              columnKey == null ? "bg-gray-100 font-medium" : "hover:bg-gray-50",
            )}
          >
            {vt("bo.import.relations.allFields", "Tous les champs")}
            <span className="text-xs text-gray-500 block">
              {totalPending} {vt("bo.import.relations.toConfirm", "à confirmer")}
            </span>
          </button>

          {groups.map((group) => (
            <button
              key={group.columnKey}
              type="button"
              onClick={() => {
                setColumnKey(group.columnKey);
                setPage(0);
              }}
              className={cn(
                "text-left px-3 py-2 rounded-lg text-sm",
                columnKey === group.columnKey
                  ? "bg-gray-100 font-medium"
                  : "hover:bg-gray-50",
              )}
            >
              <span className="flex items-center justify-between gap-2">
                <span className="truncate">{labelFor(group.columnKey)}</span>
                {group.pending > 0 ? (
                  <AlertTriangle
                    className="w-3.5 h-3.5 text-amber-600 shrink-0"
                    aria-hidden
                  />
                ) : (
                  <CheckCircle2
                    className="w-3.5 h-3.5 text-green-600 shrink-0"
                    aria-hidden
                  />
                )}
              </span>
              <span className="text-xs text-gray-500 tabular-nums">
                {group.decided}/{group.total}
              </span>
              <Progress
                value={group.total > 0 ? (group.decided / group.total) * 100 : 0}
                className="h-1 mt-1"
              />
            </button>
          ))}
        </nav>

        <div className="flex flex-col gap-3 min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            {filters.map((filter) => (
              <Badge
                key={filter.label}
                variant={state === filter.key ? "default" : "secondary"}
                className="cursor-pointer font-normal"
                onClick={() => {
                  setState(filter.key);
                  setPage(0);
                }}
              >
                {filter.label}
              </Badge>
            ))}

            <div className="ml-auto flex flex-wrap gap-2">
              <Button
                size="sm"
                variant="outline"
                disabled={bulking}
                onClick={() => bulk("allAuto", "link")}
              >
                {vt(
                  "bo.import.decisions.acceptAllRecognized",
                  "Valider les correspondances reconnues",
                )}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                disabled={bulking}
                onClick={() => bulk("allWithoutMatch", "skip")}
              >
                {vt(
                  "bo.import.decisions.emptyAllWithoutMatch",
                  "Laisser vide celles sans correspondance",
                )}
              </Button>
            </div>
          </div>

          {isLoading ? (
            <div className="flex flex-col gap-2">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          ) : (data?.items.length ?? 0) === 0 ? (
            <p className="text-sm text-gray-500 py-6">
              {vt("bo.import.decisions.emptyList", "Rien à afficher avec ce filtre.")}
            </p>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[14%]">
                      {vt("bo.import.decisions.category", "Catégorie")}
                    </TableHead>
                    <TableHead className="w-[18%]">
                      {vt("bo.import.decisions.fileValue", "Valeur de votre fichier")}
                    </TableHead>
                    <TableHead className="w-[22%]">
                      {vt("bo.import.decisions.proposed", "Correspondance proposée")}
                    </TableHead>
                    <TableHead className="w-[10%]">
                      {vt("bo.import.decisions.confidence", "Confiance")}
                    </TableHead>
                    <TableHead className="w-[26%]">
                      {vt("bo.import.decisions.yourChoice", "Votre décision")}
                    </TableHead>
                    <TableHead className="w-[10%]">
                      {vt("bo.import.decisions.state", "État")}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data?.items.map((decision) => (
                    <ImportDecisionRow
                      key={decision.id}
                      decision={decision}
                      category={labelFor(decision.columnKey)}
                      saving={answering}
                      onAnswer={(value) => void submit([value])}
                    />
                  ))}
                </TableBody>
              </Table>

              {(data?.total ?? 0) > PAGE_SIZE && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500 tabular-nums">
                    {page * PAGE_SIZE + 1}–
                    {Math.min((page + 1) * PAGE_SIZE, data?.total ?? 0)}{" "}
                    {vt("bo.import.decisions.of", "sur")} {data?.total}
                  </span>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={page === 0}
                      onClick={() => setPage((current) => current - 1)}
                    >
                      {vt("bo.import.previous", "Précédent")}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={(page + 1) * PAGE_SIZE >= (data?.total ?? 0)}
                      onClick={() => setPage((current) => current + 1)}
                    >
                      {vt("bo.import.next", "Suivant")}
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/*
        Porte vers l'ecriture : tant qu'il reste des questions, on ne passe pas.
        Une echappatoire explicite existe, mais l'utilisateur doit la choisir.
      */}
      <div className="flex flex-wrap items-center justify-between gap-3 pt-2 border-t">
        <Button variant="ghost" onClick={onBack}>
          {vt("bo.import.back", "Retour")}
        </Button>

        <div className="flex flex-wrap items-center gap-3">
          {totalPending > 0 && (
            <>
              <span className="text-sm text-amber-800">
                {totalPending}{" "}
                {vt(
                  "bo.import.relations.remaining",
                  "correspondance(s) restent à confirmer",
                )}
              </span>
              <Button
                variant="outline"
                disabled={bulking}
                onClick={() => bulk("allPending", "skip")}
              >
                {vt(
                  "bo.import.relations.skipRemaining",
                  "Laisser vide les restantes et continuer",
                )}
              </Button>
            </>
          )}
          <Button disabled={totalPending > 0} onClick={onNextStep}>
            {vt("bo.import.continue", "Continuer")}
          </Button>
        </div>
      </div>
    </div>
  );
};
